import { useState, useMemo, useCallback } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { SortDescriptor } from 'react-aria-components'
import { TableBody as AriaTableBody } from 'react-aria-components'
import { PlusIcon, Users01Icon, SearchMdIcon } from '@untitledui-icons/react/line'
import { Table, TableCard } from '@/components/application/table/table'
import { PaginationTable } from '@/components/application/pagination/pagination'
import { Button } from '@/components/base/buttons'
import { Input } from '@/components/ui/Input'
import { LoadingSpinner, EmptyState } from '@/components/common'
import { useCustomerList } from '@/hooks/api/useCustomers'
import { useListPageSearch } from '@/hooks/useListPageSearch'
import type { Customer, CustomerListFilters } from '@/types/customer'

/**
 * Customers list page - admin/partner only
 */
export function CustomersPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  // Search and pagination with stable callbacks (prevents focus loss)
  // Note: searchQuery is local state for immediate UI feedback
  // filters is derived from URL params and only updates after debounce
  const {
    searchQuery,
    handleSearch,
    filters: urlFilters,
    page,
    perPage,
    handlePageChange,
    handlePageSizeChange,
  } = useListPageSearch<CustomerListFilters>({
    useJsonFilter: false,
    paramKey: 'name',
    debounceMs: 300,
  })

  // Build filters from URL params (debounced) - NOT from local searchQuery
  const filters = useMemo((): CustomerListFilters => {
    const result: CustomerListFilters = {}
    if (urlFilters.name) {
      result.name = urlFilters.name
    }
    return result
  }, [urlFilters.name])

  // Sort state (default ascending to match v1)
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: 'id',
    direction: 'ascending',
  })

  // Fetch customers
  const { data, isLoading, isFetching } = useCustomerList({
    filters,
    page,
    perPage,
    sort: {
      field: sortDescriptor.column as string,
      order: sortDescriptor.direction === 'ascending' ? 'ASC' : 'DESC',
    },
  })

  const customers = data?.data || []
  const totalCount = data?.total || 0
  const totalPages = Math.ceil(totalCount / perPage)

  // Handle row click
  const handleRowClick = useCallback(
    (customer: Customer) => {
      navigate(`/customers/${customer.id}`)
    },
    [navigate]
  )

  // Handle sort change
  const handleSortChange = useCallback((descriptor: SortDescriptor) => {
    setSortDescriptor(descriptor)
  }, [])

  // Check if filters are active
  const hasActiveFilters = !!searchQuery

  // Show initial loading only when we have no data yet and no filters
  const showInitialLoading = isLoading && !data && !hasActiveFilters

  if (showInitialLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  // Simple empty state (no data and no filters)
  if (!data && !hasActiveFilters && customers.length === 0) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <EmptyState
          title={t('resources.customers.empty')}
          description={t('resources.customers.empty_description')}
          action={
            <Button color="primary" onClick={() => navigate('/customers/create')} iconLeading={PlusIcon}>
              {t('resources.generic.create')}
            </Button>
          }
        />
      </div>
    )
  }

  return (
    <TableCard.Root size="sm" className="flex h-full flex-col">
      <TableCard.Header
        title={t('resources.customers.overview')}
        contentTrailing={
          <div className="flex items-center gap-3">
            {/* Search */}
            <Input
              type="text"
              value={searchQuery}
              onChange={handleSearch}
              placeholder={t('resources.customers.fields.name')}
              leftIcon={<SearchMdIcon className="h-5 w-5" />}
              className="w-40 sm:w-56"
            />

            {/* Create button */}
            <Button color="primary" size="md" onClick={() => navigate('/customers/create')} iconLeading={PlusIcon}>
              <span className="hidden sm:inline">{t('resources.generic.create')}</span>
            </Button>
          </div>
        }
      />

      {/* Loading bar */}
      {isFetching && (
        <div className="h-1 w-full overflow-hidden bg-gray-100">
          <div className="h-full w-1/3 animate-pulse bg-brand-500" />
        </div>
      )}

      {/* Content area - either table or empty state */}
      {customers.length === 0 ? (
        <div className="flex flex-1 items-center justify-center p-6">
          <EmptyState
            title={t('resources.customers.empty')}
            description={t('resources.customers.empty_search')}
          />
        </div>
      ) : (
        <div className="flex-1 overflow-auto">
          <Table
            aria-label={t('resources.customers.overview')}
            sortDescriptor={sortDescriptor}
            onSortChange={handleSortChange}
          >
            <Table.Header>
              <Table.Head id="id" label={t('resources.customers.fields.id')} allowsSorting isRowHeader />
              <Table.Head id="name" label={t('resources.customers.fields.name')} allowsSorting />
              <Table.Head id="lookup_key" label={t('resources.customers.fields.lookup_key')} allowsSorting />
              <Table.Head id="actions" label="" />
            </Table.Header>

            <AriaTableBody>
              {customers.map((customer) => (
                <Table.Row
                  key={customer.id}
                  id={customer.id}
                  className="cursor-pointer"
                  onAction={() => handleRowClick(customer)}
                >
                  {/* ID */}
                  <Table.Cell>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {customer.id}
                    </span>
                  </Table.Cell>

                  {/* Name */}
                  <Table.Cell>
                    <span className="text-sm text-gray-900 dark:text-gray-100">
                      {customer.name}
                    </span>
                  </Table.Cell>

                  {/* Lookup Key */}
                  <Table.Cell>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {customer.lookup_key || '—'}
                    </span>
                  </Table.Cell>

                  {/* Actions */}
                  <Table.Cell>
                    <Link
                      to={`/customers/${customer.id}/users`}
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1.5 rounded-md border border-brand-300 bg-white px-2.5 py-1.5 text-sm font-medium text-brand-700 transition-colors hover:bg-brand-50 dark:border-brand-600 dark:bg-gray-800 dark:text-brand-400 dark:hover:bg-gray-700"
                    >
                      <Users01Icon className="h-4 w-4" />
                      <span>{t('navigation.users')}</span>
                    </Link>
                  </Table.Cell>
                </Table.Row>
              ))}
            </AriaTableBody>
          </Table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <PaginationTable
          page={page}
          total={totalPages}
          perPage={perPage}
          onPageChange={handlePageChange}
          onPerPageChange={handlePageSizeChange}
        />
      )}
    </TableCard.Root>
  )
}

export default CustomersPage
